import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useContext, useMemo, type ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import type { AnalysisIncludedSystemKey, AnalysisRecommendation, AnalysisSnapshot } from '../../../types/analysis'
import type { TimelineElement } from '../../../types/timelineElement'
import type { ChecklistItem } from '../../../types/schema'
import { TimelineContext } from '../../../context/timelineContextCore'
import { RailPageShellCard } from '../../Common/RailPageShell'
import { Pressable, StatusChip } from '../../Common'
import { MetricCard } from '../MetricCard'
import { usePoppableCard } from '../popover/usePoppableCard'

import { buildBlockerDescriptor, buildDriftDescriptor, buildMetricDescriptor, buildReadinessDescriptor } from '../popover/descriptors'
import { calculatePlannedHoursByGoal } from '../../../utils/plannedHours'

import { formatNumber } from './analysisTabShared'
import type { AnalysisNavigate } from './analysisTabShared'

export interface WorkTabProps {
  snapshot: AnalysisSnapshot
  onNavigate: AnalysisNavigate
  onOpenReports: () => void
  includedSystems?: Set<AnalysisIncludedSystemKey>
  elements?: TimelineElement[]
  checklistItems?: ChecklistItem[]
}

export const WorkTab = ({
  snapshot,
  onNavigate,
  onOpenReports,
  includedSystems,
  elements,
  checklistItems,
}: WorkTabProps) => {
  const { t } = useNls()
  const timelineCtx = useContext(TimelineContext)

  const goalHoursRows = useMemo(() => {
    const resolvedElements: TimelineElement[] = elements ?? timelineCtx?.elements ?? []
    const resolvedChecklistItems: ChecklistItem[] = checklistItems ?? timelineCtx?.checklistItems ?? []

    const goals = resolvedElements.filter((el) => el.type === 'GOAL')
    if (goals.length === 0) return []

    const plannedByGoal = calculatePlannedHoursByGoal(resolvedElements, resolvedChecklistItems)

    const rows = []
    for (const g of goals) {
      const plannedHours = plannedByGoal.get(g.id) || 0
      const actualMinutes = Number(g.actualMinutes) || 0
      const actualHours = actualMinutes / 60

      if (plannedHours > 0 || actualMinutes > 0) {
        const variance = actualHours - plannedHours
        rows.push({
          id: g.id,
          title: g.title || 'Untitled Goal',
          plannedHours,
          actualHours,
          actualMinutes,
          variance,
        })
      }
    }

    rows.sort((a, b) => b.actualHours - a.actualHours)
    return rows
  }, [elements, checklistItems, timelineCtx?.elements, timelineCtx?.checklistItems])
  const isWorkFiltered = includedSystems && !includedSystems.has('WORK')
  const workMetrics: Array<{
    title: string
    value: string
    detail: string
    icon: ReactNode
    tone?: 'default' | 'warn' | 'good'
    numeric: number
    drillTarget: AnalysisRecommendation['drillTarget'] | null
  }> = [
    {
      title: 'Throughput',
      value: `${formatNumber(snapshot.work.completed)} done`,
      detail: `${formatNumber(snapshot.work.active)} active across work + checklists`,
      icon: <TrackChangesRoundedIcon />,
      numeric: snapshot.work.completed,
      drillTarget: 'DASHBOARD',
    },
    {
      title: 'Velocity base',
      value: formatNumber(snapshot.work.checklistCompleted),
      detail: `${formatNumber(snapshot.work.checklistTotal)} checklist items tracked`,
      icon: <InsightsRoundedIcon />,
      numeric: snapshot.work.checklistCompleted,
      drillTarget: 'CHECKLISTS',
    },
    {
      title: 'Stalled',
      value: formatNumber(snapshot.work.stalled),
      detail: 'No recent local update marker',
      icon: <WarningAmberRoundedIcon />,
      tone: snapshot.work.stalled ? ('warn' as const) : ('good' as const),
      numeric: snapshot.work.stalled,
      drillTarget: 'DASHBOARD',
    },
  ]
  const blockerDescriptor = buildBlockerDescriptor(snapshot, onNavigate, onOpenReports)
  const driftDescriptor = buildDriftDescriptor(snapshot, onNavigate, onOpenReports)
  const readinessDescriptor = buildReadinessDescriptor(snapshot, onNavigate, onOpenReports)
  const { cardProps: blockerCardProps } = usePoppableCard(blockerDescriptor)
  const { cardProps: driftCardProps } = usePoppableCard(driftDescriptor)
  const { cardProps: readinessCardProps } = usePoppableCard(readinessDescriptor)
  if (isWorkFiltered) {
    return (
      <div className="analysisFilteredStateEmpty" data-testid="work-filtered-empty">
        <strong>{t('NLSAnalysisFilteredEmptyHeading')}</strong>
        <p>{t('NLSAnalysisFilteredEmptyDesc')}</p>
      </div>
    )
  }
  return (
    <div className="analysisStageStack">
      <div className="analysisMetricGrid">
        {workMetrics.map((metric) => (
          <MetricCard
            key={metric.title}
            title={metric.title}
            value={metric.value}
            detail={metric.detail}
            icon={metric.icon}
            tone={metric.tone}
            descriptor={buildMetricDescriptor({
              snapshot,
              tab: 'WORK',
              title: metric.title,
              value: metric.numeric,
              detail: metric.detail,
              drillTarget: metric.drillTarget,
              onNavigate,
              onOpenReports,
            })}
          />
        ))}
      </div>
      <RailPageShellCard {...blockerCardProps} className={`analysisDataTable${blockerCardProps.className ? ` ${blockerCardProps.className}` : ''}`} heading="Blocker intelligence" eyebrow="Flagship">
        {snapshot.blockerChains.length ? (
          snapshot.blockerChains.map((chain) => (
            <Pressable
              className="analysisClickableRow"
              key={chain.id}
              onClick={(e) => {
                e.stopPropagation()
                onNavigate('CHECKLISTS')
              }}
            >
              <StatusChip tone="neutral" size="sm">{chain.chainLength} nodes</StatusChip>
              <strong>{chain.title}</strong>
              <small>{chain.reason}</small>
              <ChevronRightRoundedIcon fontSize="small" />
            </Pressable>
          ))
        ) : (
          <StatusChip tone="success" icon={<ChecklistRoundedIcon fontSize="small" />}>
            No blocker pressure
          </StatusChip>
        )}
      </RailPageShellCard>
      <RailPageShellCard {...driftCardProps} className={`analysisDataTable${driftCardProps.className ? ` ${driftCardProps.className}` : ''}`} heading="Timeline drift" eyebrow="Schedule">
        {snapshot.driftEvents.length ? (
          snapshot.driftEvents.map((event) => (
            <Pressable
              className="analysisClickableRow"
              key={event.id}
              onClick={(e) => {
                e.stopPropagation()
                onNavigate('DASHBOARD')
              }}
            >
              <StatusChip tone="muted" size="sm">{event.severity}</StatusChip>
              <strong>{event.title}</strong>
              <small>
                {event.driftDays} day{event.driftDays === 1 ? '' : 's'} past planned end
              </small>
              <ChevronRightRoundedIcon fontSize="small" />
            </Pressable>
          ))
        ) : (
          <p>No overdue drift detected from local schedule fields.</p>
        )}
      </RailPageShellCard>
      <RailPageShellCard
        {...readinessCardProps}
        className={`analysisReadiness${readinessCardProps.className ? ` ${readinessCardProps.className}` : ''}`}
        heading="Readiness matrix"
        eyebrow="Work gates"
      >
        {snapshot.readinessMatrix.length ? (
          snapshot.readinessMatrix.map((row) => (
            <article key={row.id}>
              <strong>{row.title}</strong>
              {(['dependencies', 'proof', 'stock', 'budget'] as const).map((key) => (
                <span className={`analysisReadiness__cell is-${row[key]}`} key={key}>
                  {key}
                </span>
              ))}
            </article>
          ))
        ) : (
          <p>Nothing active needs readiness scoring.</p>
        )}
      </RailPageShellCard>
      <RailPageShellCard
        className="analysisDataTable"
        heading={t('NLSAnalysisPlannedVsActualTitle')}
        eyebrow={t('NLSAnalysisPlannedVsActualEyebrow')}
        data-testid="planned-vs-actual-hours-card"
      >
        {goalHoursRows.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {goalHoursRows.map((row) => (
              <div
                key={row.id}
                className="analysisClickableRow"
                data-testid={`goal-hours-row-${row.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderRadius: 8,
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 140, flex: 1 }}>
                  <strong style={{ display: 'block', fontSize: 13 }}>{row.title}</strong>
                  <small style={{ color: 'var(--muted)', fontSize: 12 }}>
                    {t('NLSAnalysisPlannedLabel')}: {Math.round(row.plannedHours * 10) / 10}h · {t('NLSAnalysisActualLabel')}: {Math.round(row.actualHours * 10) / 10}h
                  </small>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <StatusChip
                    tone={row.variance > 0 ? 'warning' : row.variance < 0 ? 'success' : 'neutral'}
                    size="sm"
                  >
                    {row.variance > 0 ? `+${Math.round(row.variance * 10) / 10}h` : `${Math.round(row.variance * 10) / 10}h`}
                  </StatusChip>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p data-testid="planned-vs-actual-empty" style={{ color: 'var(--muted)', fontSize: 13, margin: 0 }}>
            {t('NLSAnalysisPlannedVsActualEmpty')}
          </p>
        )}
      </RailPageShellCard>
    </div>
  )
}
